import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

// SEC-06: shown when the backend flags the account with force_password_reset
// (e.g. the super admin whose credential was exposed). Blocks the portal until
// a new password is set.
export default function ForcePasswordChange() {
  const { setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (next.length < 12) { setErr("Use at least 12 characters."); return; }
    if (next !== confirm) { setErr("New passwords do not match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (e2) {
      const d = e2?.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Could not change password. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const forgot = async () => {
    try { await logout(); } catch { /* ignore */ }
    navigate("/forgot-password");
  };
  const signOut = async () => {
    try { await logout(); } catch { /* ignore */ }
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-navy px-4" data-testid="force-password-change">
      <form onSubmit={submit} className="w-full max-w-md bg-white rounded-xl shadow-xl p-8 space-y-5">
        <div>
          <span className="eyebrow text-gold">Security</span>
          <h1 className="text-2xl font-semibold text-navy mt-1">Set a new password</h1>
          <p className="text-sm text-neutral-600 mt-2">
            For your account's security you must choose a new password before continuing.
            Use at least 12 characters and avoid simple "Word@Year" patterns.
          </p>
        </div>
        <div className="space-y-3">
          <Input type="password" placeholder="Current password" value={current}
                 onChange={(e) => setCurrent(e.target.value)} required
                 data-testid="fpc-current" autoComplete="current-password" />
          <Input type="password" placeholder="New password (min 12 characters)" value={next}
                 onChange={(e) => setNext(e.target.value)} required
                 data-testid="fpc-new" autoComplete="new-password" />
          <Input type="password" placeholder="Confirm new password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} required
                 data-testid="fpc-confirm" autoComplete="new-password" />
        </div>
        {err && <p className="text-sm text-red-600" data-testid="fpc-error">{err}</p>}
        <Button type="submit" disabled={busy} className="w-full" data-testid="fpc-submit">
          {busy ? "Saving…" : "Update password"}
        </Button>
        <div className="flex items-center justify-between pt-1 text-xs">
          <button type="button" onClick={forgot} className="text-navy underline" data-testid="fpc-forgot">
            Don't know your current password?
          </button>
          <button type="button" onClick={signOut} className="text-neutral-500 hover:text-navy" data-testid="fpc-signout">
            Sign out
          </button>
        </div>
      </form>
    </div>
  );
}
